<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import {
    ARCHIVE_SCOPE_OPTIONS,
    WORK_ORDER_ACTION_REQUIRED_STATE_OPTIONS,
    WORK_ORDER_OPERATIONAL_STATE_OPTIONS,
    createDefaultWorkOrderFilters,
    dateRangeFiltersEqual,
    normalizeWorkOrderFilterValue,
    type FilterDisplayMode,
    type WorkOrderActionRequiredState,
    type WorkOrderEmployeeFilterOption,
    type WorkOrderFilterValue,
    type WorkOrderOperationalState,
  } from "../../_shared/_logic/work-order-filters"

  export type {
    FilterDisplayMode,
    WorkOrderActionRequiredState,
    WorkOrderEmployeeFilterOption,
    WorkOrderFilterValue,
    WorkOrderOperationalState,
  } from "../../_shared/_logic/work-order-filters"

  interface Props {
    value?: WorkOrderFilterValue
    orderTypes?: OrderType[]
    employees?: WorkOrderEmployeeFilterOption[]
    loading?: boolean
    filterMode?: FilterDisplayMode
    onChange?: (value: any) => void
    onReset?: () => void
  }

  let {
    value = $bindable<WorkOrderFilterValue>(createDefaultWorkOrderFilters()),
    orderTypes = [],
    employees = [],
    loading = false,
    filterMode = "compact",
    onChange = () => {},
    onReset = () => {},
  }: Props = $props()

  const sanitizeValue = normalizeWorkOrderFilterValue

  const arraysEqual = (a: unknown[] = [], b: unknown[] = []) => {
    if (a.length !== b.length) return false

    return a.every((entry, index) => Object.is(entry, b[index]))
  }

  const valuesEqual = (a: WorkOrderFilterValue | null, b: WorkOrderFilterValue | null) => {
    if (!a || !b) return false

    return (
      a.searchQuery === b.searchQuery &&
      arraysEqual(a.orderTypeIds, b.orderTypeIds) &&
      dateRangeFiltersEqual(a.appointmentDateRange, b.appointmentDateRange) &&
      dateRangeFiltersEqual(a.dueDateRange, b.dueDateRange) &&
      arraysEqual(a.employeeIds, b.employeeIds) &&
      arraysEqual(a.workOrderStates, b.workOrderStates) &&
      a.actionRequiredState === b.actionRequiredState &&
      a.archiveScope === b.archiveScope
    )
  }

  let lastEmittedValue: WorkOrderFilterValue | null = null

  const emitChange = () => {
    const normalized = sanitizeValue(value)

    if (valuesEqual(lastEmittedValue, normalized)) return

    lastEmittedValue = normalized
    onChange?.(normalized)
  }

  const workOrderStateOptions = $derived(WORK_ORDER_OPERATIONAL_STATE_OPTIONS)
  const actionRequiredStateOptions = $derived(WORK_ORDER_ACTION_REQUIRED_STATE_OPTIONS)
  const archiveScopeOptions = $derived(ARCHIVE_SCOPE_OPTIONS)
  const orderTypeOptions = $derived(
    orderTypes.map((type) => ({
      name: type.internal_label || "Ohne Bezeichnung",
      value: type.id,
    })),
  )
  const employeeOptions = $derived(
    employees.map((employee) => ({
      name: employee.label,
      value: employee.id,
    })),
  )
</script>

<div class="form">
  <div class="row">
    <Input
      type="text"
      placeholder="Suche (z. B. Musterstr 12 20095)"
      bind:value={value.searchQuery}
      brightBackground={true}
      disabled={loading}
      tooltip="Volltextsuche über Auftrag, Liegenschaft, Ansprechpartner und Termininfos. Mehrere Begriffe inkl. Zahlen werden kombiniert."
      onChange={emitChange}
    />
    <Input
      type="select"
      placeholder="Zustand"
      multiple={true}
      bind:value={value.workOrderStates}
      options={workOrderStateOptions}
      brightBackground={true}
      disabled={loading}
      tooltip="Filtert nach fachlichen Zuständen wie ohne Termin, Ankündigung läuft oder Auftrag gesichert angekündigt."
      onChange={emitChange}
    />
    <Input
      type="dateRange"
      placeholder="Terminzeitraum"
      bind:value={value.appointmentDateRange}
      brightBackground={true}
      disabled={loading}
      dateGranularity="day"
      tooltip="Filtert nach dem Terminzeitraum des zugewiesenen Termins."
      onChange={emitChange}
    />
    <Input
      type="select"
      placeholder="Mitarbeiter"
      multiple={true}
      bind:value={value.employeeIds}
      options={employeeOptions}
      brightBackground={true}
      disabled={loading}
      tooltip="Filtert Aufträge nach dem Mitarbeitenden des zugewiesenen Termins."
      onChange={emitChange}
    />
  </div>
  {#if filterMode === "detailed"}
    <div class="row detail-row">
      <Input
        type="select"
        placeholder="Auftragstyp"
        multiple={true}
        bind:value={value.orderTypeIds}
        options={orderTypeOptions}
        brightBackground={true}
        disabled={loading}
        tooltip="Filtert nach Auftragstyp."
        onChange={emitChange}
      />
      <Input
        type="dateRange"
        placeholder="Fälligkeit"
        bind:value={value.dueDateRange}
        brightBackground={true}
        disabled={loading}
        dateGranularity="day"
        tooltip="Filtert nach Fälligkeitszeitraum."
        onChange={emitChange}
      />
      <Input
        type="select"
        placeholder="Aktion erforderlich"
        bind:value={value.actionRequiredState}
        options={actionRequiredStateOptions}
        brightBackground={true}
        disabled={loading}
        tooltip="Filtert danach, ob für den Auftrag aktuell eine Aktion nötig ist."
        onChange={emitChange}
      />
      <Input
        type="select"
        placeholder="Archivstatus"
        bind:value={value.archiveScope}
        options={archiveScopeOptions}
        brightBackground={true}
        disabled={loading}
        tooltip="Zeigt aktive, archivierte oder alle Aufträge."
        onChange={emitChange}
      />
    </div>
  {/if}
</div>

<style lang="less">
  .form {
    margin: 0;
  }

  .detail-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.2rem;
    align-items: center;

    :global(.select),
    :global(.date-range),
    :global(.input-container) {
      min-width: 0;
      width: 100%;
    }
  }
</style>
